<script lang="ts">
	import {
		Button,
		Container,
		Datepicker,
		IconButton,
		Select,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { dateFormat } from 'src/helpers/date_helper';
	import {
		dayModelSelectOptionForStorageIndex,
		handleSelectChange,
		parseHolidayDate,
		todayDateString,
		type TariffTables,
	} from '../helpers/daisy_em_tariff_helpers';

	export let tariffTables: TariffTables;
	export let formatD: string;

	$: activeDayIndices = tariffTables.days.reduce<number[]>((indices, pattern, dayIdx) => {
		if (pattern.some(Boolean)) indices.push(dayIdx);
		return indices;
	}, []);

	$: holidayDates = tariffTables.holidays.map((h) => (h ? parseHolidayDate(h[0]) : null));

	$: dayModelOptions = activeDayIndices.map((_, i) => ({
		value: `D${i + 1}`,
		title: $format('daisy_em_tariff_day_model_option', {
			values: { n: i + 1 },
			default: `Day model ${i + 1}`,
		}),
	}));

	/**
	 * Handles the Datepicker change event for a holiday date, storing it as "YYYY-MM-DD".
	 * Ignores the event when the resolved value is not a valid Date.
	 * @param i - Holiday slot index.
	 * @param e - Change event from the Datepicker component.
	 */
	const handleHolidayDateChange = (holidayIdx: number, event: CustomEvent) => {
		const date = (event.detail as Date[][])[0]?.[0] ?? (event.detail as Date[][])[1];
		if (date instanceof Date && !isNaN(date.getTime())) {
			const rate = (tariffTables.holidays[holidayIdx] as [string, number])[1];
			tariffTables.holidays[holidayIdx] = [dateFormat(date, 'Y-m-d'), rate];
			tariffTables = { ...tariffTables };
		}
	};

	/**
	 * Handles the Select change event for a holiday's day pattern assignment.
	 * @param i - Holiday slot index.
	 * @param e - Change event from the Select component.
	 */
	const handleHolidayRateChange = (holidayIdx: number, event: CustomEvent) => {
		const compressedIdx = activeDayIndices
			.map((_, dayIdx) => `D${dayIdx + 1}`)
			.indexOf(handleSelectChange(event));
		const dateStr = (tariffTables.holidays[holidayIdx] as [string, number])[0];
		tariffTables.holidays[holidayIdx] = [
			dateStr,
			activeDayIndices[compressedIdx] ?? activeDayIndices[0] ?? 0,
		];
		tariffTables = { ...tariffTables };
	};

	/**
	 * Clears a holiday slot, making it available for a new entry.
	 * @param i - Holiday slot index to remove.
	 */
	const removeHoliday = (i: number) => {
		tariffTables.holidays[i] = null;
		tariffTables = { ...tariffTables };
	};

	/** Fills the first empty holiday slot with today's date and day pattern 0. */
	const addHoliday = () => {
		const idx = tariffTables.holidays.findIndex((h) => h === null);
		if (idx !== -1) {
			tariffTables.holidays[idx] = [todayDateString(), 0];
			tariffTables = { ...tariffTables };
		}
	};
</script>

<Text styleType="h2" class="mb-s mt-m">
	{$format('daisy_em_tariff_holidays', { default: 'Holidays' })}
</Text>
<Container type="borderSolid" backgroundColor="color-base" class="mb-s">
	<Stack gap="xs" class="mb-s">
		{#each tariffTables.holidays as holiday, i}
			{#if holiday !== null}
				<div class="d-flex align-items-center gap-xs">
					<Datepicker
						id={`holiday-date-${i}`}
						dateFormat={formatD}
						mode="single"
						value={holidayDates[i] ?? undefined}
						aria-label={$format('daisy_em_tariff_holiday_date', {
							default: 'Holiday date',
						})}
						a11yProps={{
							inputAriaLabel: $format('input_enter_date', { default: 'Enter date' }),
							selectMonthAriaLabel: $format('select_month', {
								default: 'Select month',
							}),
							selectYearAriaLabel: $format('select_year', { default: 'Select year' }),
						}}
						on:change={(e) => handleHolidayDateChange(i, e)}
					/>
					<Select
						id={`holiday-${i}`}
						aria-label={$format('daisy_em_tariff_holiday_day_pattern', {
							default: 'Day pattern for holiday',
						})}
						size="m"
						icon="fas fa-calendar-day"
						value={dayModelSelectOptionForStorageIndex(
							dayModelOptions,
							activeDayIndices,
							holiday[1],
						)}
						options={dayModelOptions}
						on:change={(e) => handleHolidayRateChange(i, e)}
					/>
					<IconButton
						variant="secondary"
						aria-label={$format('daisy_em_tariff_remove_holiday', {
							default: 'Remove holiday',
						})}
						icon="fas fa-xmark"
						on:click={() => removeHoliday(i)}
					/>
				</div>
			{/if}
		{/each}
	</Stack>
	{#if tariffTables.holidays.some((h) => h === null)}
		<Button
			size="s"
			variant="primary"
			icon="fas fa-plus"
			styleType="ghost"
			width="full"
			on:click={addHoliday}
		>
			{$format('daisy_em_tariff_add_holiday', { default: 'Add holiday' })}
		</Button>
	{/if}
</Container>
